import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getTimeOutTime } from "services/api/query";
import useAuthService from "services/authService";
import { removeAuthInfo } from "store/slices";
import { inactivityWarningTimeInSec } from "utils/constants";
import { dataDogErrorLog, handleRefId } from "utils/helpers";
import TimeoutWarningModal from "./TimeoutWarningModal";

const InactivityTimeoutComponent = () => {
  const authService = useAuthService();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [timeoutMinutes, setTimeoutMinutes] = useState(0);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [remainingTimeInSec, setRemainingTimeInSec] = useState(
    inactivityWarningTimeInSec
  );

  // Constants
  const WARNING_DURATION_MS = inactivityWarningTimeInSec * 1000;

  useEffect(() => {
    const fetchTime = async () => {
      try {
        const time = await getTimeOutTime();
        const minutes = time
          ? Number(time)
          : Number(process.env.REACT_APP_TIMEOUT_IN_MIN);
        setTimeoutMinutes(minutes);
      } catch (error) {
        const { refId, message } = handleRefId(error);
        dataDogErrorLog(
          message,
          ``,
          {
            error
          },
          refId
        );
        this.setState({ refId });
        setTimeoutMinutes(Number(process.env.REACT_APP_TIMEOUT_IN_MIN));
      }
    };
    fetchTime();
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("lastActivity");
    authService.logOut();
    dispatch({ type: "Reset" });
    dispatch(removeAuthInfo());
    navigate("/login");
  }, [authService, dispatch, navigate]);

  const updateLastActivity = useCallback(() => {
    if (!showWarningModal) {
      localStorage.setItem("lastActivity", Date.now().toString());
    }
  }, [showWarningModal]);

  useEffect(() => {
    const eventTypes = [
      "keypress",
      "mousemove",
      "mousedown",
      "scroll",
      "touchmove",
      "pointermove"
    ];

    eventTypes.forEach((type) =>
      window.addEventListener(type, updateLastActivity)
    );

    return () => {
      eventTypes.forEach((type) =>
        window.removeEventListener(type, updateLastActivity)
      );
    };
  }, [updateLastActivity]);

  useEffect(() => {
    if (!timeoutMinutes) return;

    const TIMEOUT_MS = timeoutMinutes * 60 * 1000;

    if (!localStorage.getItem("lastActivity")) {
      localStorage.setItem("lastActivity", Date.now().toString());
    }

    const checkInactivity = () => {
      const lastActivity = parseInt(
        localStorage.getItem("lastActivity") || Date.now(),
        10
      );
      const now = Date.now();
      const timeElapsed = now - lastActivity;

      if (timeElapsed >= TIMEOUT_MS + WARNING_DURATION_MS) {
        handleLogout();
        return;
      }

      if (timeElapsed >= TIMEOUT_MS) {
        if (!showWarningModal) setShowWarningModal(true);

        const timeLeftMs = TIMEOUT_MS + WARNING_DURATION_MS - timeElapsed;
        const timeLeftSec = Math.ceil(timeLeftMs / 1000);

        setRemainingTimeInSec(timeLeftSec > 0 ? timeLeftSec : 0);
      } else {
        if (showWarningModal) {
          setShowWarningModal(false);
        }
      }
    };

    checkInactivity();

    const intervalId = setInterval(checkInactivity, 1000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkInactivity();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [timeoutMinutes, WARNING_DURATION_MS, handleLogout, showWarningModal]);

  const onCloseModal = () => {
    setShowWarningModal(false);
    setRemainingTimeInSec(inactivityWarningTimeInSec);
    localStorage.setItem("lastActivity", Date.now().toString());
  };

  if (!showWarningModal) return null;

  return (
    <TimeoutWarningModal
      isModalOpen={showWarningModal}
      onCloseModal={onCloseModal}
      remainingTimeInSec={remainingTimeInSec}
    />
  );
};

export default InactivityTimeoutComponent;
